import type { PieDatum } from '@vnetwork/ui/charts';
import type { BarListItem } from '@vnetwork/ui/data';
import type { TimelineItem, TimelineTone } from '@vnetwork/ui/data';
import { ArrowRight } from 'lucide-react';
import type {
  DashboardStats,
  SupplierStat,
  ProcessTrend,
  WarehouseProcess,
  ProcessType,
  ProcessStatus,
} from '@/api/capacities';
import { formatMoney, formatDateTime } from '@/lib/format';
import { TableLink } from '@/components/table-link';
import { humanizeKey } from './capacity-format';
import { PROCESS_TYPE_LABEL, PROCESS_STATUS_LABEL } from '../processes/process-format';

/**
 * Chart-data builders for the Physical Inventory dashboard. Pure functions that
 * shape `DashboardStats` into @vnetwork/ui chart/list props. Series colors use
 * semantic solid tokens only (never raw `--vn-color-*`) so charts stay legible
 * and theme-reactive.
 */

/** Rotating semantic-solid palette for categorical slices/bars. */
const SLICE_COLORS = [
  'var(--vn-primary-solid)',
  'var(--vn-success-solid)',
  'var(--vn-warning-solid)',
  'var(--vn-info-solid)',
  'var(--vn-danger-solid)',
  'var(--vn-accent-solid, var(--vn-primary-solid))',
];

/**
 * Fixed color per lifecycle status, matching the legacy dashboard:
 * Available = green, In Use = blue, Loaned = purple. Any status outside this
 * map falls back to the rotating {@link SLICE_COLORS} palette.
 */
const STATUS_COLORS: Record<string, string> = {
  available: 'var(--vn-success-solid)', // green
  in_use: 'var(--vn-primary-solid)', // blue
  loaned: 'var(--vn-accent-solid)', // purple slot
};

/**
 * Canonical status ordering — legacy renders available → in_use → loaned first
 * (insertion order from the API), NOT sorted by value. Unlisted statuses keep
 * their original order after these.
 */
const STATUS_ORDER = ['available', 'in_use', 'loaned'];

/** Row for a per-status colored bar chart (one series key per status). */
export interface StatusBarData {
  /** Data rows — each row carries a value only under its own status key. */
  rows: Array<Record<string, string | number>>;
  /** One series per status so recharts colors each bar independently. */
  series: Array<{ key: string; label: string; color: string }>;
}

/**
 * Pivot a status Record into a per-status colored bar chart. Each status
 * becomes its own recharts series and its own x bucket, so every bar gets its
 * fixed status color (Available green / In Use blue / Loaned purple) instead of
 * one flat series color. Statuses are ordered available → in_use → loaned.
 */
export function toStatusBars(record: Record<string, number> | undefined): StatusBarData {
  const entries = Object.entries(record ?? {}).filter(([, v]) => Number(v) > 0);
  const rank = (key: string) => {
    const i = STATUS_ORDER.indexOf(key);
    return i === -1 ? STATUS_ORDER.length : i;
  };
  entries.sort((a, b) => rank(a[0]) - rank(b[0]));

  const series = entries.map(([key], i) => ({
    key,
    label: humanizeKey(key),
    color: STATUS_COLORS[key] ?? SLICE_COLORS[i % SLICE_COLORS.length],
  }));
  const rows = entries.map(([key, value]) => ({
    label: humanizeKey(key),
    [key]: Number(value),
  }));
  return { rows, series };
}

/**
 * Location distribution → per-location colored bars. Legacy renders these bars
 * multi-colored (one hue per location) rather than a single flat series, so we
 * pivot each location into its own series/bucket and cycle {@link SLICE_COLORS}.
 */
export function toLocationBars(record: Record<string, number> | undefined): StatusBarData {
  const entries = Object.entries(record ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1]);

  const series = entries.map(([key], i) => ({
    key,
    label: humanizeKey(key),
    color: SLICE_COLORS[i % SLICE_COLORS.length],
  }));
  const rows = entries.map(([key, value]) => ({
    label: humanizeKey(key),
    [key]: Number(value),
  }));
  return { rows, series };
}

/** Donut slices (label + value + rotating semantic color) from a Record. */
export function toPieSlices(record: Record<string, number> | undefined): PieDatum[] {
  return Object.entries(record ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value], i) => ({
      name: humanizeKey(key),
      value: Number(value),
      color: SLICE_COLORS[i % SLICE_COLORS.length],
    }));
}

/** BarList rows (label + value + rotating color) from a Record. */
export function toBarRows(record: Record<string, number> | undefined): BarListItem[] {
  return Object.entries(record ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value], i) => ({
      label: humanizeKey(key),
      value: Number(value),
      color: SLICE_COLORS[i % SLICE_COLORS.length],
    }));
}

/** Top suppliers → BarList rows, value = equipment count, subtitle = total value. */
export function toSupplierBars(suppliers: SupplierStat[] | undefined): BarListItem[] {
  return [...(suppliers ?? [])]
    .filter((s) => Number(s.count) > 0)
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .slice(0, 10)
    .map((s) => ({
      label: s.supplier_name || '—',
      value: Number(s.count ?? 0),
      display: `${s.count ?? 0} · ${formatMoney(s.total_value ?? 0)}`,
      color: 'var(--vn-primary-solid)',
    }));
}

/** Process-trend rows for the stacked import/export/transfer bar chart. */
export function toTrendRows(trend: ProcessTrend[] | undefined) {
  return (trend ?? []).map((t) => ({
    label: t.month,
    import: Number(t.import ?? 0),
    export: Number(t.export ?? 0),
    transfer: Number(t.transfer ?? 0),
  }));
}

/** Warranty alert card definitions — count + severity dot per bucket. */
export interface WarrantyCard {
  key: string;
  label: string;
  value: number;
  /** Severity swatch token (Stat `dotColor`). */
  dotColor: string;
}

const TONE_SOLID = {
  warning: 'var(--vn-warning-solid)',
  danger: 'var(--vn-danger-solid)',
  neutral: 'var(--vn-border-strong)',
} as const;

export function toWarrantyCards(w: DashboardStats['warranty_expiring'] | undefined): WarrantyCard[] {
  const warranty = w ?? {};
  return [
    { key: '30', label: 'Expiring ≤ 30 days', value: Number(warranty.expiring_30_days ?? 0), dotColor: TONE_SOLID.warning },
    { key: '60', label: 'Expiring ≤ 60 days', value: Number(warranty.expiring_60_days ?? 0), dotColor: TONE_SOLID.warning },
    { key: '90', label: 'Expiring ≤ 90 days', value: Number(warranty.expiring_90_days ?? 0), dotColor: TONE_SOLID.warning },
    { key: 'expired', label: 'Already Expired', value: Number(warranty.already_expired ?? 0), dotColor: TONE_SOLID.danger },
    { key: 'none', label: 'No Warranty Info', value: Number(warranty.no_warranty_info ?? 0), dotColor: TONE_SOLID.neutral },
  ];
}

const PROCESS_TYPE_TONE: Record<string, TimelineTone> = {
  import_purchase: 'success',
  import_return: 'success',
  export_sale: 'warning',
  export_loan: 'warning',
  export_delivery: 'warning',
  internal_transfer: 'info',
  inventory_check: 'neutral',
};

const PROCESS_STATUS_TONE: Partial<Record<ProcessStatus, TimelineTone>> = {
  completed: 'success',
  cancelled: 'danger',
  pending_approval: 'warning',
};

function typeLabel(type: ProcessType): string {
  return PROCESS_TYPE_LABEL[type] ?? humanizeKey(type);
}

/** Recent processes → Timeline items (code + type, source→destination, time). */
export function toRecentTimeline(processes: WarehouseProcess[] | undefined): TimelineItem[] {
  return (processes ?? []).slice(0, 10).map((p) => {
    const from = p.source?.name;
    const to = p.destination?.name;
    const route =
      from || to ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
          {from ?? '—'} <ArrowRight size={12} /> {to ?? '—'}
        </span>
      ) : null;
    return {
      id: p.id,
      title: (
        <TableLink to={`/capacities/processes/${p.id}`} title={p.code}>
          {p.code} · {typeLabel(p.type)}
        </TableLink>
      ),
      meta: PROCESS_STATUS_LABEL[p.status] ?? humanizeKey(p.status),
      description: route,
      timestamp: p.created_at ? formatDateTime(p.created_at) : undefined,
      tone: PROCESS_STATUS_TONE[p.status] ?? PROCESS_TYPE_TONE[p.type] ?? 'neutral',
    };
  });
}
